/**
 * Single source of truth for plan prices shown on the landing page.
 * Centralized so future Stripe wiring imports cents/labels from one place.
 *
 * Rules: changing these constants changes every UI surface that imports them.
 * Do not duplicate amounts inline in components.
 */
export const PLAN_PRICES = {
  monthly:   { amount: 29,  period: "month",    label: "Monthly",   priceId: "plan_monthly_eur"   },
  quarterly: { amount: 69,  period: "3 months", label: "Quarterly", priceId: "plan_quarterly_eur" },
  yearly:    { amount: 149, period: "year",     label: "Yearly",    priceId: "plan_yearly_eur"    },
} as const;

export type PlanKey = keyof typeof PLAN_PRICES;

/** Maps a Stripe price lookup_key to the internal subscription tier name. */
export const tierForPriceId: Record<string, "monthly" | "quarterly" | "yearly"> = {
  plan_monthly_eur:   "monthly",
  plan_quarterly_eur: "quarterly",
  plan_yearly_eur:    "yearly",
};

export const MONTHLY_ANNUAL_EQUIVALENT =
  PLAN_PRICES.monthly.amount * 12; // €348

/** Rounded % saved vs paying monthly for a year. */
export const yearlySavingsPct = () =>
  Math.round((1 - PLAN_PRICES.yearly.amount / MONTHLY_ANNUAL_EQUIVALENT) * 100);

/** Rounded % saved vs paying monthly for a full quarter-cycle year. */
export const quarterlySavingsPct = () =>
  Math.round((1 - (PLAN_PRICES.quarterly.amount * 4) / MONTHLY_ANNUAL_EQUIVALENT) * 100);

/**
 * Shared "what's included with every paid plan" list used by both the
 * Landing pricing section and the Pricing page. All features below are
 * unlocked for every paid tier — plans only differ by access duration.
 *
 * Terminology is the canonical wording used across Features, Pricing,
 * Dashboard, and Marketing surfaces. The first four entries render as
 * the primary checklist inside each pricing card; everything beyond is
 * summarised by the "+ Keywords, flashcards, explanations and more"
 * tagline shown under the list.
 */
export const PLAN_INCLUDED_FEATURES: readonly string[] = [
  "Chapter-Based Practice",
  "Keywords & Flashcards",
  "Timed Mock Exams",
  "Performance Analytics",
  "Readiness Insights",
  "Detailed Explanations",
];
